/* __next_internal_client_entry_do_not_use__ FaireErrorBoundary,FaireErrorBoundaryErrorContent auto */ import { _ as _call_super } from "@swc/helpers/_/_call_super";
import { _ as _class_call_check } from "@swc/helpers/_/_class_call_check";
import { _ as _inherits } from "@swc/helpers/_/_inherits";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { isSupportedBrowser } from "@faire/web--source/common/browser";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { logError, logFatal } from "@faire/web--source/common/logging";
import { faireBaseUrl } from "@faire/web--source/common/routes/faireBaseUrl";
import { isError } from "@faire/web--source/common/typescriptUtils";
import { Column } from "@faire/web--source/slate/Layout";
import { FaireLogo } from "@faire/web--source/slate/logos/FaireLogo";
import { GrowingSpacer, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Anchor } from "@faire/web--source/ui/LinkAnchor";
import * as React from "react";
import styled from "styled-components";
import { ContactFormLinkFormatting } from "../ContactFormLink";
var BACKGROUND_IMAGE = "https://cdn.faire.com/res/hszgttpjt/image/upload/error_page/PF-Lifestyle-12/1546985268.jpg";
var createFingerPrint = function(error) {
    var _error_message_includes, _error_message;
    // Want to group react hook violations to prevent multiple sentry issues
    //   Because we minify our code, we cannot directly search for the error message
    // TODO should be extracted into @faire/web--source/common once we can verify it's doing what we want
    if ((_error_message = error.message) === null || _error_message === void 0 ? void 0 : (_error_message_includes = _error_message.includes) === null || _error_message_includes === void 0 ? void 0 : _error_message_includes.call(_error_message, "React error #321")) {
        return [
            "invalid-react-hook"
        ];
    }
    return undefined;
};
/**
 * An app-level error boundary that serves as a global "catch all" for errors
 * thrown during rendering. The fallback content is designed to be displayed
 * in the entire window.
 */ export var FaireErrorBoundary = /*#__PURE__*/ function(_React_Component) {
    "use strict";
    _inherits(FaireErrorBoundary, _React_Component);
    function FaireErrorBoundary(props) {
        _class_call_check(this, FaireErrorBoundary);
        var _this;
        _this = _call_super(this, FaireErrorBoundary, [
            props
        ]);
        _this.state = {
            error: props.error || null
        };
        return _this;
    }
    var _proto = FaireErrorBoundary.prototype;
    _proto.componentDidMount = function componentDidMount() {
        var _this = this;
        var history = this.props.history;
        this.unListen = history.listen(function() {
            _this.setState({
                error: null
            });
        });
    };
    _proto.componentWillUnmount = function componentWillUnmount() {
        if (this.unListen) {
            this.unListen();
        }
    };
    _proto.componentDidCatch = function componentDidCatch(error, errorInfo) {
        var errorName = undefined;
        var errorMessage = undefined;
        if (error instanceof Error) {
            errorName = error === null || error === void 0 ? void 0 : error.name;
            errorMessage = error === null || error === void 0 ? void 0 : error.message;
        }
        var isCSSLoadError = errorMessage === null || errorMessage === void 0 ? void 0 : errorMessage.includes("Loading CSS chunk");
        if (isCSSLoadError) {
            // We can ignore the CSS load error, as it doesn't make the page un-functional,
            // and we don't really have much to do for it
            return;
        }
        // For other network related errors, we still want to crash the page as it does
        // make the page un-functional
        this.setState({
            error: error
        });
        var isNetworkError = errorMessage === null || errorMessage === void 0 ? void 0 : errorMessage.includes("Network Error");
        var isChunkLoadError = errorName === "ChunkLoadError";
        if (isNetworkError || isChunkLoadError) {
            // But since Datadog RUM track network errors, so we don't want to duplicate logs inside
            // the ErrorBoundary.
            return;
        }
        if (this.props.logFatal) {
            var _this_props_onFatalError, _this_props;
            logFatal(error, {
                data: errorInfo,
                fingerprint: createFingerPrint(error)
            });
            (_this_props_onFatalError = (_this_props = this.props).onFatalError) === null || _this_props_onFatalError === void 0 ? void 0 : _this_props_onFatalError.call(_this_props, error);
        } else {
            logError(error, {
                data: errorInfo
            });
        }
    };
    _proto.render = function render() {
        if (isError(this.state.error)) {
            return /*#__PURE__*/ _jsx(FaireErrorBoundaryErrorContent, _object_spread({}, this.props, this.state));
        }
        return this.props.children;
    };
    return FaireErrorBoundary;
}(React.Component);
export var FaireErrorBoundaryErrorContent = function(props) {
    if (getWindow().envName !== "production" && !props.storybook) {
        return /*#__PURE__*/ _jsx(StackTraceWrapper, {
            children: props.error.stack ? props.error.stack : props.error.message
        });
    }
    if (!props.fullPage) {
        var _props_customMessage;
        return /*#__PURE__*/ _jsxs(InlineErrorWrapper, {
            children: [
                /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "1iEBQS",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Sorry, something went wrong."
                        }
                    ]
                }),
                /*#__PURE__*/ _jsx("br", {}),
                (_props_customMessage = props.customMessage) !== null && _props_customMessage !== void 0 ? _props_customMessage : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "oiNDot",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Please refresh the page or try again later."
                        }
                    ]
                }),
                /*#__PURE__*/ _jsx("br", {}),
                props.helpCenterPage ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "QkDqRU",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "If the problem persists, please contact us at:"
                                }
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("span", {
                            children: [
                                /*#__PURE__*/ _jsx(Anchor, {
                                    href: "https://www.instagram.com/faire_wholesale/",
                                    target: "_blank",
                                    rel: "noreferrer",
                                    children: "Instagram"
                                }),
                                /*#__PURE__*/ _jsx("br", {}),
                                /*#__PURE__*/ _jsx(Anchor, {
                                    href: "https://www.facebook.com/FaireWholesale/",
                                    target: "_blank",
                                    rel: "noreferrer",
                                    children: "Facebook"
                                }),
                                /*#__PURE__*/ _jsx("br", {}),
                                /*#__PURE__*/ _jsx(Anchor, {
                                    href: "https://twitter.com/faire_wholesale/",
                                    target: "_blank",
                                    rel: "noreferrer",
                                    children: "Twitter"
                                })
                            ]
                        })
                    ]
                }) : /*#__PURE__*/ _jsx("span", {
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "wNaaCp",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "If the problem persists, please "
                            },
                            {
                                type: 8,
                                value: "ContactFormLinkFormatting",
                                children: [
                                    {
                                        type: 0,
                                        value: "contact us"
                                    }
                                ]
                            }
                        ],
                        values: {
                            ContactFormLinkFormatting: ContactFormLinkFormatting
                        }
                    })
                })
            ]
        });
    }
    var _props_customMessage1;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(StickyHeader, {
                children: /*#__PURE__*/ _jsx(HeaderContainer, {
                    children: /*#__PURE__*/ _jsx(Logo, {
                        children: /*#__PURE__*/ _jsx(Anchor, {
                            href: faireBaseUrl(),
                            children: /*#__PURE__*/ _jsx(FaireLogo, {
                                size: 128
                            })
                        })
                    })
                })
            }),
            /*#__PURE__*/ _jsx(BackgroundImage, {}),
            /*#__PURE__*/ _jsxs(Wrapper, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: {
                            mobileAndAbove: 115,
                            tabletAndAbove: 445,
                            desktopAndAbove: 245
                        }
                    }),
                    !location.host.includes("faire") && !location.host.includes("localhost") && !props.storybook ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "displayLSerifRegular",
                                color: Core.text.critical,
                                children: "Oops!"
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: "7x"
                            }),
                            /*#__PURE__*/ _jsxs(BodyWrapper, {
                                children: [
                                    /*#__PURE__*/ _jsx(Anchor, {
                                        href: "https://faire.com",
                                        children: /*#__PURE__*/ _jsxs(Typography, {
                                            variant: "paragraphSansMedium",
                                            children: [
                                                /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                                    id: "mk3r++",
                                                    defaultMessage: [
                                                        {
                                                            type: 0,
                                                            value: "It looks like you're using an offline version of Faire"
                                                        }
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("br", {}),
                                                /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                                    id: "VyE9TW",
                                                    defaultMessage: [
                                                        {
                                                            type: 0,
                                                            value: "Please visit Faire.com to continue."
                                                        }
                                                    ]
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        size: "4x"
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraphSansMedium",
                                        color: Core.text.subdued,
                                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                            id: "AmkbF7",
                                            defaultMessage: [
                                                {
                                                    type: 0,
                                                    value: "Still having problems?"
                                                },
                                                {
                                                    type: 1,
                                                    value: "lineBreak"
                                                },
                                                {
                                                    type: 0,
                                                    value: "Please "
                                                },
                                                {
                                                    type: 8,
                                                    value: "ContactFormLinkFormatting",
                                                    children: [
                                                        {
                                                            type: 0,
                                                            value: "contact us"
                                                        }
                                                    ]
                                                },
                                                {
                                                    type: 0,
                                                    value: "."
                                                }
                                            ],
                                            values: {
                                                ContactFormLinkFormatting: ContactFormLinkFormatting
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        size: "10x"
                                    })
                                ]
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "displayLSerifRegular",
                                color: Core.text.critical,
                                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                    id: "iq/W7O",
                                    defaultMessage: [
                                        {
                                            type: 0,
                                            value: "Oops!"
                                        },
                                        {
                                            type: 1,
                                            value: "lineBreak"
                                        },
                                        {
                                            type: 0,
                                            value: "It\u2019s not you, it\u2019s us."
                                        }
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: "7x"
                            }),
                            /*#__PURE__*/ _jsxs(BodyWrapper, {
                                children: [
                                    /*#__PURE__*/ _jsxs(Typography, {
                                        variant: "paragraphSansMedium",
                                        children: [
                                            /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                                id: "1iEBQS",
                                                defaultMessage: [
                                                    {
                                                        type: 0,
                                                        value: "Sorry, something went wrong."
                                                    }
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("br", {}),
                                            (_props_customMessage1 = props.customMessage) !== null && _props_customMessage1 !== void 0 ? _props_customMessage1 : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                                id: "oiNDot",
                                                defaultMessage: [
                                                    {
                                                        type: 0,
                                                        value: "Please refresh the page or try again later."
                                                    }
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        size: "4x"
                                    }),
                                    !isSupportedBrowser() ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "paragraphSansMedium",
                                                color: Core.text.subdued,
                                                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                                    id: "KgTtxx",
                                                    defaultMessage: [
                                                        {
                                                            type: 0,
                                                            value: "Please try these browsers:"
                                                        }
                                                    ]
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs(BrowserList, {
                                                children: [
                                                    /*#__PURE__*/ _jsx("li", {
                                                        children: /*#__PURE__*/ _jsx(Typography, {
                                                            variant: "paragraphSansMedium",
                                                            children: /*#__PURE__*/ _jsx(Anchor, {
                                                                href: "https://www.google.ca/chrome/",
                                                                children: "Google Chrome"
                                                            })
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx("li", {
                                                        children: /*#__PURE__*/ _jsx(Typography, {
                                                            variant: "paragraphSansMedium",
                                                            children: /*#__PURE__*/ _jsx(Anchor, {
                                                                href: "https://www.mozilla.org/en-US/firefox/new/",
                                                                children: "Firefox"
                                                            })
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx("li", {
                                                        children: /*#__PURE__*/ _jsx(Typography, {
                                                            variant: "paragraphSansMedium",
                                                            children: /*#__PURE__*/ _jsx(Anchor, {
                                                                href: "https://www.apple.com/ca/safari/",
                                                                children: "Safari"
                                                            })
                                                        })
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(GrowingSpacer, {})
                                        ]
                                    }) : null,
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        height: "2x"
                                    }),
                                    props.helpCenterPage ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "paragraphSansMedium",
                                                color: Core.text.subdued,
                                                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                                    id: "QkDqRU",
                                                    defaultMessage: [
                                                        {
                                                            type: 0,
                                                            value: "If the problem persists, please contact us at:"
                                                        }
                                                    ]
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs(BrowserList, {
                                                children: [
                                                    /*#__PURE__*/ _jsx("li", {
                                                        children: /*#__PURE__*/ _jsx(Typography, {
                                                            variant: "paragraphSansMedium",
                                                            children: /*#__PURE__*/ _jsx(Anchor, {
                                                                href: "https://www.instagram.com/faire_wholesale/",
                                                                target: "_blank",
                                                                rel: "noreferrer",
                                                                children: "Instagram"
                                                            })
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx("li", {
                                                        children: /*#__PURE__*/ _jsx(Typography, {
                                                            variant: "paragraphSansMedium",
                                                            children: /*#__PURE__*/ _jsx(Anchor, {
                                                                href: "https://www.facebook.com/FaireWholesale/",
                                                                target: "_blank",
                                                                rel: "noreferrer",
                                                                children: "Facebook"
                                                            })
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx("li", {
                                                        children: /*#__PURE__*/ _jsx(Typography, {
                                                            variant: "paragraphSansMedium",
                                                            children: /*#__PURE__*/ _jsx(Anchor, {
                                                                href: "https://twitter.com/faire_wholesale/",
                                                                target: "_blank",
                                                                rel: "noreferrer",
                                                                children: "Twitter"
                                                            })
                                                        })
                                                    })
                                                ]
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraphSansMedium",
                                        color: Core.text.subdued,
                                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                            id: "AmkbF7",
                                            defaultMessage: [
                                                {
                                                    type: 0,
                                                    value: "Still having problems?"
                                                },
                                                {
                                                    type: 1,
                                                    value: "lineBreak"
                                                },
                                                {
                                                    type: 0,
                                                    value: "Please "
                                                },
                                                {
                                                    type: 8,
                                                    value: "ContactFormLinkFormatting",
                                                    children: [
                                                        {
                                                            type: 0,
                                                            value: "contact us"
                                                        }
                                                    ]
                                                },
                                                {
                                                    type: 0,
                                                    value: "."
                                                }
                                            ],
                                            values: {
                                                ContactFormLinkFormatting: ContactFormLinkFormatting
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        size: "10x"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
var StackTraceWrapper = styled.div.withConfig({
    componentId: "sc-bf0724c8-0"
})([
    "font-size:13px;line-height:1.5rem;margin:30px;white-space:30px;color:#aa0000;"
]);
var InlineErrorWrapper = styled(Column).attrs({
    justify: "center"
}).withConfig({
    componentId: "sc-bf0724c8-1"
})([
    "text-align:center;width:100%;padding:13px;background-color:rgba(255,109,109,0.2);"
]);
var BodyWrapper = styled(Column).withConfig({
    componentId: "sc-bf0724c8-2"
})([
    "flex-grow:1;padding-left:114px;padding-top:0;@media screen and (max-width:767px){padding-left:0;}@media screen and (min-width:768px) and (max-width:1023px){padding-left:37vw;padding-top:calc(60vh - 600px);}"
]);
var Wrapper = styled(Column).withConfig({
    componentId: "sc-bf0724c8-3"
})([
    "height:100vh;max-width:1024px;padding-left:30px;margin:0 auto;a{color:",
    ";}"
], Core.text.default);
var SIDEBAR_WIDTH = 200;
var HEADER_HEIGHT = 60;
var Logo = styled.div.withConfig({
    componentId: "sc-bf0724c8-4"
})([
    "display:inline-block;width:",
    "px;text-align:center;"
], SIDEBAR_WIDTH);
var HeaderContainer = styled.div.withConfig({
    componentId: "sc-bf0724c8-5"
})([
    "display:flex;align-items:center;height:",
    "px;position:relative;"
], HEADER_HEIGHT);
var StickyHeader = styled.div.withConfig({
    componentId: "sc-bf0724c8-6"
})([
    "z-index:1;background:white;border-bottom:1px solid #ccc;position:fixed;top:0;width:100vw;"
]);
var BrowserList = styled.ul.withConfig({
    componentId: "sc-bf0724c8-7"
})([
    "letter-spacing:0;list-style-type:none;padding-left:0;"
]);
var BackgroundImage = styled.div.withConfig({
    componentId: "sc-bf0724c8-8"
})([
    "z-index:-1;position:absolute;top:0;width:calc(50vw + 100px);left:calc(50vw - 100px);height:100vh;background:url(",
    ") center / cover no-repeat;@media screen and (max-width:767px){background:none;}@media screen and (min-width:768px) and (max-width:1023px){height:60vh;}"
], BACKGROUND_IMAGE);
